// One voice note, photo, video or document inside a thread.
//
// The link is fetched from the Worker (whatsapp/getMediaUrl), which checks the
// media id is one we received and asks the whatsapp-media edge function for a
// one hour signed URL to a private copy. The API token never reaches the
// browser, and no file is ever public.
//
// Voice notes and photos load as soon as they are shown, because a voice note
// you have to click twice to hear is a voice note nobody listens to. Videos and
// documents wait for a click, because they can be large.
import { useCallback, useEffect, useState } from 'react'
import { FileText, Film, RotateCw } from 'lucide-react'
import { useGetMediaUrl } from '../../hooks/backend/whatsapp'
import { Spinner } from '../../lib/shadcn/spinner'
import type { MediaKind, ThreadMessage } from '../../utils/whatsapp'

type MediaUrl = { url: string; mime_type: string | null; filename: string | null }

export function MediaAttachment({
  message,
  kind,
}: {
  message: ThreadMessage
  kind: Exclude<MediaKind, null>
}) {
  const { trigger } = useGetMediaUrl<MediaUrl>()
  const [state, setState] = useState<{ url: string | null; loading: boolean; error: string | null }>({
    url: null,
    loading: false,
    error: null,
  })
  const eager = kind === 'audio' || kind === 'image'

  const load = useCallback(async () => {
    setState({ url: null, loading: true, error: null })
    try {
      const res = await trigger({ media_id: message.media_id }, { skipCache: true })
      setState({
        url: res?.url ?? null,
        loading: false,
        error: res?.url ? null : 'No link came back for this file.',
      })
      return res?.url ?? null
    } catch (e) {
      setState({ url: null, loading: false, error: e instanceof Error ? e.message : String(e) })
      return null
    }
  }, [trigger, message.media_id])

  useEffect(() => {
    if (eager) void load()
  }, [eager, load])

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
        <Spinner className="h-3.5 w-3.5" /> Fetching the {kind === 'audio' ? 'voice note' : kind}…
      </div>
    )
  }
  if (state.error) {
    return (
      <div className="flex items-start gap-2 py-1 text-xs text-rose-700 dark:text-rose-300">
        <span className="min-w-0 flex-1">{state.error}</span>
        <button
          type="button"
          onClick={() => void load()}
          className="shrink-0 underline"
          aria-label="Try again"
        >
          <RotateCw className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  if (kind === 'audio') {
    return state.url ? (
      <audio controls preload="metadata" src={state.url} className="h-10 w-64 max-w-full" />
    ) : null
  }
  if (kind === 'image') {
    return state.url ? (
      <a href={state.url} target="_blank" rel="noreferrer">
        <img
          src={state.url}
          alt="Photo sent on WhatsApp"
          className="max-h-72 max-w-full rounded-md object-contain"
        />
      </a>
    ) : null
  }
  if (kind === 'video') {
    return state.url ? (
      <video controls preload="metadata" src={state.url} className="max-h-72 max-w-full rounded-md" />
    ) : (
      <button
        type="button"
        onClick={() => void load()}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs hover:bg-accent"
      >
        <Film className="h-3.5 w-3.5" /> Load video
      </button>
    )
  }
  // Document: a click fetches a fresh link and opens it, so a link that has
  // aged past its hour is never the one clicked.
  return (
    <button
      type="button"
      onClick={async () => {
        const url = state.url ?? (await load())
        if (url) window.open(url, '_blank', 'noopener')
      }}
      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs hover:bg-accent"
    >
      <FileText className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{message.filename ?? 'Download document'}</span>
    </button>
  )
}
